import QRCode from 'qrcode';

export interface PaymentParams {
  /**
   * Organization ID. Defaults to `000000` for merchant-generated QR codes.
   */
  orgId?: string;

  /**
   * Transaction note or description, typically displayed to the payer.
   */
  tn?: string;

  /**
   * URL of the bank or merchant's website for reference.
   */
  url?: string;

  /**
   * Payee's UPI ID, which uniquely identifies the recipient in the UPI ecosystem.
   */
  pa: string;

  /**
   * Payee's name, which is displayed to the payer for confirmation.
   */
  pn: string;

  /**
   * Merchant category code, which identifies the type of business.
   * Example: `5812` typically refers to eating places or restaurants.
   */
  mc?: string;

  /**
   * Currency code for the transaction.
   * Example: `INR` for Indian Rupees.
   */
  cu?: string;

  /**
   * Merchant ID, a unique identifier assigned to the merchant by the payment system.
   */
  mid?: string;

  /**
   * Merchant terminal ID.
   */
  mtid?: string;

  /**
   * Transaction reference ID (order/bill number). Required for dynamic merchant QR.
   */
  tr?: string;

  /**
   * Transaction initiation mode. Defaults to `01` for QR-based payments.
   */
  mode?: string;
}

function buildUpiQuery(params: PaymentParams, amount?: number, tr?: string): string {
  const query: Record<string, string> = {
    orgId: params.orgId || '000000',
    mode: params.mode || '01',
    pa: params.pa,
    pn: params.pn,
  };

  if (params.mc) query.mc = params.mc;
  if (params.cu) query.cu = params.cu;
  if (params.tn) query.tn = params.tn;
  if (params.url) query.url = params.url;
  if (params.mid) query.mid = params.mid;
  if (params.mtid) query.mtid = params.mtid;

  const transactionRef = tr || params.tr;
  if (transactionRef) query.tr = String(transactionRef);

  if (amount !== undefined && amount !== null && amount > 0) {
    query.am = amount.toFixed(2);
    if (!query.cu) query.cu = 'INR';
  }

  return Object.entries(query)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

export async function generatePaymentQR(
  params: PaymentParams,
  amount?: number,
  tr?: string
): Promise<string> {
  try {
    if (!params?.pa || !params?.pn) {
      throw new Error('Payee UPI ID (pa) and name (pn) are required');
    }

    const upiUrl = `upi://pay?${buildUpiQuery(params, amount, tr)}`;

    const qrCodeDataUri = await QRCode.toDataURL(upiUrl, { errorCorrectionLevel: 'H', width: 300 });

    return qrCodeDataUri;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
}
